import React from "react";
import { Badge, Button, Col, Row, Spinner } from "react-bootstrap";

import CsLineIcons from "../../../cs-line-icons";
import { formatCents, frequencyLabel, WIZARD_STEPS } from "./state";

const PRODUCT_ICONS = {
    "Retail.Matters": "suitcase",
    "Retail.Calendar": "calendar",
    "Retail.Chat.Slack": "messages",
    "Retail.Email": "email",
    "Retail.Render": "book",
    "Retail.Sign": "edit",
    "Retail.Survey": "clipboard"
};

const ProductIcon = ({ productId, size = 24 }) => (
    <span className="product-order-icon" aria-hidden="true">
        <CsLineIcons icon={PRODUCT_ICONS[productId] || "box"} size={size} />
    </span>
);

export const WizardProgress = ({ currentStep, canVisit, onStep }) => (
    <nav className="product-order-progress" aria-label="Product order progress">
        {WIZARD_STEPS.map((step, index) => {
            const complete = step.id < currentStep;
            const active = step.id === currentStep;
            return (
                <React.Fragment key={step.id}>
                    <button
                        type="button"
                        className={`product-order-progress-step ${active ? "active" : ""} ${complete ? "complete" : ""}`}
                        aria-current={active ? "step" : undefined}
                        disabled={!canVisit(step.id)}
                        onClick={() => onStep(step.id)}
                    >
                        <span className="product-order-progress-number" aria-hidden="true">
                            {complete ? <CsLineIcons icon="check" size={16} /> : step.id}
                        </span>
                        <span>{step.label}</span>
                    </button>
                    {index < WIZARD_STEPS.length - 1 ? <span className="product-order-progress-line" aria-hidden="true" /> : null}
                </React.Fragment>
            );
        })}
    </nav>
);

const ProductSkeletons = () => (
    <Row className="g-3" aria-label="Loading products">
        {[0, 1, 2].map(index => (
            <Col xs="12" md="6" lg="4" key={index}>
                <div className="product-order-card product-order-skeleton" />
            </Col>
        ))}
    </Row>
);

export const ProductSelectionStep = ({
    products,
    selectedProductIds,
    includedProductIds,
    loading,
    error,
    recommendations,
    onToggle,
    onRetry
}) => (
    <section aria-labelledby="product-order-step-heading">
        <div className="product-order-step-intro">
            <h2 id="product-order-step-heading" tabIndex="-1">Choose products</h2>
            <p>Select one or more Feradel products. You will choose a plan for each product next.</p>
        </div>
        {error ? (
            <div className="alert alert-danger" role="alert">
                {error} <Button variant="link" className="p-0 align-baseline" onClick={onRetry}>Try again</Button>
            </div>
        ) : null}
        {loading ? <ProductSkeletons /> : (
            <Row className="g-3 product-order-grid">
                {products.map(product => {
                    const selected = selectedProductIds.includes(product.id);
                    const included = includedProductIds.includes(product.id);
                    return (
                        <Col xs="12" md="6" lg="4" key={product.id}>
                            <button
                                type="button"
                                className={`product-order-card ${selected || included ? "selected" : ""}`}
                                aria-pressed={selected || included}
                                aria-describedby={`product-${product.id}-description`}
                                disabled={included}
                                onClick={() => onToggle(product.id)}
                            >
                                <span className="product-order-card-heading">
                                    <ProductIcon productId={product.id} />
                                    <span className="product-order-card-name">{product.name}</span>
                                </span>
                                <span id={`product-${product.id}-description`} className="product-order-card-description">
                                    {product.description}
                                </span>
                                <span className="product-order-card-footer">
                                    <span className="product-order-starting-price">
                                        {product.freeAvailable
                                            ? "Free plan available"
                                            : `From ${formatCents(product.startingPriceCents)}`}
                                    </span>
                                    <span className={`btn btn-sm ${selected || included ? "btn-primary" : "btn-outline-primary"}`}>
                                        {included ? "Included" : selected ? "Remove" : "Add"}
                                    </span>
                                </span>
                                {included ? <span className="product-order-included-note">Already licensed on this account</span> : null}
                            </button>
                        </Col>
                    );
                })}
            </Row>
        )}
        {recommendations.length > 0 ? (
            <aside className="product-order-recommendations" aria-labelledby="product-order-recommendations-heading">
                <h3 id="product-order-recommendations-heading">Recommended for you</h3>
                <Row className="g-3">
                    {recommendations.map(recommendation => (
                        <Col xs="12" md="6" key={recommendation.productId}>
                            <div className="product-order-recommendation">
                                <ProductIcon productId={recommendation.productId} size={20} />
                                <div className="flex-grow-1">
                                    <strong>{recommendation.name}</strong>
                                    <div className="text-muted text-small">{recommendation.reason}</div>
                                </div>
                                <Button size="sm" variant="outline-primary" onClick={() => onToggle(recommendation.productId)}>Add</Button>
                            </div>
                        </Col>
                    ))}
                </Row>
            </aside>
        ) : null}
    </section>
);

const PlanBadges = ({ plan, current }) => (
    <span className="product-order-plan-badges">
        {current ? <Badge bg="success">Current</Badge> : null}
        {plan.default ? <Badge bg="secondary">Default</Badge> : null}
        {plan.featured ? <Badge bg="primary">Recommended</Badge> : null}
        {plan.priceCents === 0 && !plan.custom ? <Badge bg="light" text="dark" className="border">Free</Badge> : null}
        {plan.custom ? <Badge bg="secondary">Custom</Badge> : null}
    </span>
);

export const PlanSelectionStep = ({
    products,
    selectedProductIds,
    selectedPlanByProductId,
    currentPlanId,
    planErrors,
    restrictToInitialProduct,
    onPlan,
    onRemove
}) => (
    <section aria-labelledby="product-order-step-heading">
        <div className="product-order-step-intro">
            <h2 id="product-order-step-heading" tabIndex="-1">Select plans</h2>
            <p>Choose exactly one plan for each selected product. Nothing is submitted until you confirm the order.</p>
        </div>
        <div className="product-order-plan-groups">
            {selectedProductIds.map(productId => {
                const product = products.find(item => item.id === productId);
                if (!product) return null;
                const errorId = `product-${productId}-plan-error`;
                return (
                    <section
                        className={`product-order-plan-group ${planErrors[productId] ? "has-error" : ""}`}
                        key={productId}
                        id={`product-${productId}-plans`}
                        aria-describedby={planErrors[productId] ? errorId : undefined}
                    >
                        <div className="product-order-plan-group-heading">
                            <div className="d-flex align-items-center gap-2">
                                <ProductIcon productId={productId} />
                                <div>
                                    <h3 id={`product-${productId}-heading`}>{product.name}</h3>
                                    <div className="text-muted text-small">{product.description}</div>
                                </div>
                            </div>
                            {!restrictToInitialProduct ? (
                                <Button variant="link" size="sm" onClick={() => onRemove(productId)}>Remove product</Button>
                            ) : null}
                        </div>
                        <div role="radiogroup" aria-labelledby={`product-${productId}-heading`}>
                            {product.plans.map(plan => {
                                const selected = selectedPlanByProductId[productId] === plan.id;
                                const current = currentPlanId === plan.id;
                                return (
                                    <label className={`product-order-plan-row ${selected ? "selected" : ""} ${plan.custom || plan.disabled ? "disabled" : ""}`} key={plan.id}>
                                        <input
                                            type="radio"
                                            name={`plan-${productId}`}
                                            value={plan.id}
                                            checked={selected}
                                            disabled={plan.custom || plan.disabled}
                                            aria-describedby={plan.disabledReason ? `plan-${plan.id}-unavailable` : undefined}
                                            onChange={() => onPlan(productId, plan.id)}
                                        />
                                        <span className="product-order-plan-copy">
                                            <span className="product-order-plan-title">
                                                <strong>{product.name} {plan.name}</strong>
                                                <PlanBadges plan={plan} current={current} />
                                            </span>
                                            <span className="text-muted text-small">{plan.allowance || plan.description || "Feradel product license"}</span>
                                            {plan.disabledReason ? <span id={`plan-${plan.id}-unavailable`} className="text-small">{plan.disabledReason}</span> : null}
                                        </span>
                                        <span className="product-order-plan-frequency">{frequencyLabel(plan.frequency)}</span>
                                        <span className="product-order-plan-price">
                                            {plan.custom ? "Contact Sales" : formatCents(plan.priceCents)}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                        {planErrors[productId] ? <div id={errorId} className="invalid-feedback d-block">{planErrors[productId]}</div> : null}
                    </section>
                );
            })}
        </div>
    </section>
);

const TotalRow = ({ label, amount, strong = false }) => (
    <div className={`product-order-total-row ${strong ? "total" : ""}`}>
        <span>{label}</span><span>{formatCents(amount)}</span>
    </div>
);

const displayText = (value, fallback = "") => (
    typeof value === "string" || typeof value === "number" ? String(value) : fallback
);

export const ReviewOrderStep = ({ estimate, existingLicenseId }) => {
    const hasPayment = Number(estimate?.dueToday || 0) > 0;
    return (
        <section aria-labelledby="product-order-step-heading" aria-live="polite">
            <div className="product-order-step-intro">
                <h2 id="product-order-step-heading" tabIndex="-1">Review &amp; pay</h2>
                <p>Review every product and plan before {existingLicenseId ? "confirming this change" : "placing your order"}.</p>
            </div>
            <div className="product-order-review-table" role="table" aria-label="Order summary">
                <div className="product-order-review-header" role="row">
                    <span>Product / Plan</span><span>Details</span><span>Frequency</span><span>Price</span>
                </div>
                {(estimate?.lineItems || []).map(line => (
                    <div className="product-order-review-row" role="row" key={`${line.productId}-${line.planId}`}>
                        <span><strong>{displayText(line.productName, "Feradel product")}</strong><small>{displayText(line.planName, line.planCode || "Plan")}</small></span>
                        <span>{displayText(line.description, "Feradel license")}{line.current ? <Badge bg="success" className="ms-2">Current</Badge> : null}</span>
                        <span>{frequencyLabel(line.frequency)}</span>
                        <span>{formatCents(line.recurringAmount || line.dueTodayAmount)}</span>
                    </div>
                ))}
            </div>
            <Row className="g-4 mt-1 justify-content-end">
                <Col xs="12" lg="5">
                    {hasPayment ? (
                        <div className="product-order-payment-note">
                            <CsLineIcons icon="lock-on" size={20} />
                            <div><strong>Secure payment with Stripe</strong><div className="text-muted text-small">You can select or add a payment method in the secure checkout.</div></div>
                        </div>
                    ) : (
                        <div className="alert alert-info mb-0">
                            No credit card is required for free plans. Free usage resets each billing period and may be suspended when the allowance is exhausted.
                        </div>
                    )}
                </Col>
                <Col xs="12" lg="5">
                    <div className="product-order-totals">
                        <TotalRow label="Subtotal" amount={estimate?.subtotal} />
                        {estimate?.discountTotal ? <TotalRow label="Discounts" amount={-estimate.discountTotal} /> : null}
                        {estimate?.creditTotal ? <TotalRow label="Credits" amount={-estimate.creditTotal} /> : null}
                        {estimate?.proratedTotal ? <TotalRow label="Proration" amount={estimate.proratedTotal} /> : null}
                        {estimate?.taxTotal ? <TotalRow label="Tax" amount={estimate.taxTotal} /> : null}
                        <TotalRow label="Total Due Today" amount={estimate?.dueToday} strong />
                        <TotalRow label="Recurring Plan Total" amount={estimate?.recurringTotal} />
                        {estimate?.nextBillingDate ? (
                            <div className="product-order-total-row"><span>Next Billing Date</span><span>{new Date(estimate.nextBillingDate).toLocaleDateString()}</span></div>
                        ) : null}
                    </div>
                </Col>
            </Row>
        </section>
    );
};

export const LoadingLabel = ({ children }) => (
    <span className="d-inline-flex align-items-center gap-2"><Spinner animation="border" size="sm" />{children}</span>
);
